import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { colors2, icons, UI_CAPTION_DF, UI_TAG } from "oolib";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";

import Draggable from "@/components/generalUI/Draggable";
import { SearchInput } from "@/components/UI/SearchInput";
import { __GetContentTypeConfigNew } from "@/utils/getters/gettersV2";
import { useGetAnnotationsLazy } from "@/utils/react-query-hooks/getData";
import { useDebounce } from "@/utils/customHooks";

import { ConfigForm_SectionWrapper } from "../../../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import {
  ANNOTATION_SEARCH_CONFIG,
  ANNOTATION_TRANSLATION_CONFIG,
  annotationSnippet,
  buildPickedAnnotationsFilter,
  toPickedEntry,
} from "../pickedAnnotations";

const MIN_SEARCH_LENGTH = 2;

/** The dataset's human title, falling back to its key — same lookup AnnoCard does. */
const contentTypeDisplay = (contentType) =>
  __GetContentTypeConfigNew(contentType)?.general?.content?.title || contentType;

const RESULTS_PER_PAGE = 20;

const StyledScrollList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: ${({ $maxHeight = "32rem" }) => $maxHeight};
  overflow-y: auto;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem;
  border: 1px solid ${({ $selected }) => ($selected ? colors2.primaryContainer : colors2.grey10)};
  border-radius: 0.6rem;
  cursor: ${({ $static }) => ($static ? "default" : "pointer")};
  background: ${({ $selected }) => ($selected ? "rgba(7, 118, 237, 0.04)" : "transparent")};

  &:hover {
    border-color: ${({ $static }) => ($static ? colors2.grey10 : colors2.grey30)};
  }
`;

const StyledRowText = styled.div`
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

/**
 * Annotation text runs to whole paragraphs, so rows clamp rather than truncate
 * at a character count: the quote stays readable at whatever width the config
 * panel happens to be, and no word is cut mid-way.
 */
const StyledClampedText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: ${({ $lines }) => $lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/**
 * The config panel for TempPickedAnnotations: search every annotation, see the
 * real text, tick the ones to keep.
 *
 * Search is server-side (Elastic, via useGetAnnotationsLazy) because 70k
 * annotations can't be shipped to the client and filtered locally — this is the
 * same query the Annotation Explorer page runs, on the same fields.
 *
 * The picked list is stored with a text snippet alongside each id (see
 * ../pickedAnnotations.js) so this panel can render the selection without a
 * second fetch, and can still name an annotation that has since been deleted.
 */
export const AnnotationPicker = ({ props, updateProps }) => {
  const picked = props.picked || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [committedTerm, setCommittedTerm] = useState("");
  const runDebounced = useDebounce({ interval: 400 });

  const handleSearchChange = (_key, value) => {
    setSearchTerm(value);
    // Typing fires a request per keystroke otherwise, against the whole index.
    runDebounced(() => setCommittedTerm(value));
  };

  const searchEnabled = committedTerm.trim().length >= MIN_SEARCH_LENGTH;

  const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetAnnotationsLazy({
      dataSource: "annotations",
      activeFilters: [],
      searchConfig: ANNOTATION_SEARCH_CONFIG,
      searchTerm: committedTerm,
      translationConfig: ANNOTATION_TRANSLATION_CONFIG,
      limit: RESULTS_PER_PAGE,
      queryId: "tempPickedAnnotations_picker",
      queryOptions: { enabled: searchEnabled },
    });

  const results = useMemo(
    () => (data?.pages || []).flatMap((page) => page?.data || []),
    [data]
  );

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.value)), [picked]);

  /**
   * The picked annotations' CURRENT text, fetched by id.
   *
   * The stored `display` snippet is a cache, and a cache goes stale: entries
   * saved before this panel preferred fragment text still hold an observation
   * title, and an annotation's text can be edited after it was picked. Reading
   * the live text means the Selected list always shows what the rendered card
   * shows, with no re-picking.
   *
   * The stored snippet stays as the fallback — it is the only thing left to
   * show for an annotation that has since been deleted.
   */
  const pickedIdList = useMemo(() => [...pickedIds], [pickedIds]);

  const { data: pickedData } = useGetAnnotationsLazy({
    dataSource: "annotations",
    activeFilters: pickedIdList.length ? [buildPickedAnnotationsFilter(pickedIdList)] : [],
    limit: Math.max(pickedIdList.length, 1),
    translationConfig: ANNOTATION_TRANSLATION_CONFIG,
    queryId: "tempPickedAnnotations_selected",
    queryOptions: { enabled: pickedIdList.length > 0 },
  });

  const liveSnippetById = useMemo(() => {
    const map = new Map();
    (pickedData?.pages || []).forEach((page) =>
      (page?.data || []).forEach((anno) => map.set(String(anno._id), annotationSnippet(anno)))
    );
    return map;
  }, [pickedData]);

  const togglePick = (anno) => {
    const entry = toPickedEntry(anno);
    updateProps((p) => {
      const current = p.picked || [];
      return {
        ...p,
        picked: current.some((d) => d.value === entry.value)
          ? current.filter((d) => d.value !== entry.value)
          : [...current, entry], // appended, so picked order IS render order
      };
    });
  };

  const removePick = (value) =>
    updateProps((p) => ({
      ...p,
      picked: (p.picked || []).filter((d) => d.value !== value),
    }));

  /**
   * Drag-and-drop reorder. Draggable hands back the whole reordered array, and
   * the widget renders in exactly this order — so the list IS the layout.
   */
  const handleReposition = (newAry) => updateProps((p) => ({ ...p, picked: newAry }));

  return (
    <>
      <ConfigForm_SectionWrapper icon="MagnifyingGlass" title="Find Annotations">
        <SearchInput
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search annotation text"
        />
        <SearchResults
          enabled={searchEnabled}
          status={status}
          results={results}
          pickedIds={pickedIds}
          onToggle={togglePick}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      </ConfigForm_SectionWrapper>

      <ConfigForm_SectionWrapper
        icon="Quotes"
        title={picked.length ? `Selected (${picked.length})` : "Selected"}
      >
        {picked.length === 0 ? (
          <UI_CAPTION_DF style={{ color: colors2.grey50 }}>
            Nothing picked yet — search above and tick the annotations to show.
          </UI_CAPTION_DF>
        ) : (
          <StyledScrollList>
            <Draggable
              ary={picked}
              idValPath="value"
              enableDraggableHandle={true}
              handleReposition={handleReposition}
              style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}
              renderChildren={(entry) => (
                <StyledRow $static>
                  <StyledRowText>
                    <StyledClampedText $lines={2}>
                      <UI_CAPTION_DF style={{ color: colors2.grey80 }}>
                        {liveSnippetById.get(entry.value) || entry.display}
                      </UI_CAPTION_DF>
                    </StyledClampedText>
                  </StyledRowText>
                  <ButtonGhost
                    icon="X"
                    hoverColor="red"
                    onClick={() => removePick(entry.value)}
                    style={{ padding: "2px" }}
                  />
                </StyledRow>
              )}
            />
          </StyledScrollList>
        )}
      </ConfigForm_SectionWrapper>
    </>
  );
};

const SearchResults = ({
  enabled,
  status,
  results,
  pickedIds,
  onToggle,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}) => {
  if (!enabled) {
    return (
      <UI_CAPTION_DF style={{ color: colors2.grey50 }}>
        Type at least {MIN_SEARCH_LENGTH} characters to search.
      </UI_CAPTION_DF>
    );
  }
  if (status === "loading") {
    return <UI_CAPTION_DF style={{ color: colors2.grey50 }}>Searching…</UI_CAPTION_DF>;
  }
  if (status === "error") {
    return <UI_CAPTION_DF style={{ color: colors2.red }}>Could not search annotations.</UI_CAPTION_DF>;
  }
  if (!results.length) {
    return <UI_CAPTION_DF style={{ color: colors2.grey50 }}>No annotations match.</UI_CAPTION_DF>;
  }

  const CheckIcon = icons.CheckCircle;
  const EmptyIcon = icons.Circle;

  return (
    <StyledScrollList $maxHeight="40rem">
      {results.map((anno) => {
        const id = String(anno._id);
        const selected = pickedIds.has(id);
        const Icon = selected ? CheckIcon : EmptyIcon;
        return (
          <StyledRow
            key={id + anno.annoKey}
            $selected={selected}
            onClick={() => onToggle(anno)}
          >
            {Icon && (
              <Icon
                size={16}
                weight={selected ? "fill" : "regular"}
                color={selected ? colors2.primaryContainer : colors2.grey40}
                style={{ flexShrink: 0, marginTop: "0.2rem" }}
              />
            )}
            <StyledRowText>
              <UI_TAG style={{ color: colors2.grey50 }}>
                {contentTypeDisplay(anno?.meta?.contentType)}
              </UI_TAG>
              <StyledClampedText $lines={4}>
                <UI_CAPTION_DF style={{ color: colors2.grey80 }}>
                  {annotationSnippet(anno)}
                </UI_CAPTION_DF>
              </StyledClampedText>
            </StyledRowText>
          </StyledRow>
        );
      })}
      {hasNextPage && (
        <ButtonGhost
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          children={isFetchingNextPage ? "Loading…" : "Load more"}
        />
      )}
    </StyledScrollList>
  );
};
